import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useLocation, useNavigate } from "react-router";
import { authApi } from "@/lib/api";
import { useAuth } from "@/providers/AuthProvider";
import { useI18n } from "@/hooks/useI18n";
import { Button } from "@/components/ui/Button";
import { Field, Input } from "@/components/ui/Field";
import { Alert } from "@/components/ui/Alert";
import { resendErrorMessage, verifyErrorMessage } from "./authErrors";

const CODE_PATTERN = /^\d{6}$/;
const RESEND_COOLDOWN_SECONDS = 60;

interface LocationState {
    email?: string;
    /** Set by SignupPage — shows the "code sent" notice once. */
    justRegistered?: boolean;
}

export function VerifyEmailPage() {
    const { t } = useI18n();
    const navigate = useNavigate();
    const location = useLocation();
    const { coach, refreshProfile, logout, isAuthenticated } = useAuth();
    const [leaving, setLeaving] = useState(false);

    const locationState = (location.state as LocationState | null) ?? null;
    const stateEmail = locationState?.email ?? "";
    const [email, setEmail] = useState(stateEmail || coach?.email || "");
    const [code, setCode] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(() =>
        locationState?.justRegistered && stateEmail
            ? t("auth.signup.otpSent", { email: stateEmail })
            : null,
    );
    const [submitting, setSubmitting] = useState(false);
    const [resending, setResending] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
        return () => clearTimeout(timer);
    }, [cooldown]);

    async function handleSubmit(event: FormEvent) {
        event.preventDefault();
        const trimmedEmail = email.trim().toLowerCase();
        const trimmedCode = code.trim();
        if (!trimmedEmail) {
            setError(t("auth.verify.emailRequired"));
            return;
        }
        if (!CODE_PATTERN.test(trimmedCode)) {
            setError(t("auth.verify.codeInvalid"));
            return;
        }

        setError(null);
        setNotice(null);
        setSubmitting(true);
        try {
            const result = await authApi.verifyEmail(trimmedEmail, trimmedCode);
            // `alreadyVerified` tanpa sesi — suruh login biasa.
            if (result.alreadyVerified) {
                navigate("/login", { replace: true, state: { email: trimmedEmail } });
                return;
            }
            // Verify fresh menerbitkan sesi pertama (token sudah tersimpan
            // di authApi.verifyEmail) — sinkronkan profil lalu ke /plan.
            // Gagal sync (network) → ke /login bawa email, sesi tetap ada
            // dan AuthAlreadyRedirect akan mengantar ke /plan.
            setNotice(t("auth.verify.verifiedOk"));
            try {
                await refreshProfile();
            } catch {
                navigate("/login", { replace: true, state: { email: trimmedEmail } });
                return;
            }
            navigate("/plan", { replace: true });
        } catch (caught) {
            const mapped = verifyErrorMessage(caught, t);
            // OTP gagal fatal (akun tak ada) -> bersih sesi, balik ke login.
            // Salah kode biasa tetap di sini biar bisa resend.
            if (mapped.fatal404) {
                try {
                    if (isAuthenticated) await logout();
                } finally {
                    navigate("/login", { replace: true });
                }
                return;
            }
            setError(mapped.text);
        } finally {
            setSubmitting(false);
        }
    }

    async function handleResend() {
        const trimmedEmail = email.trim().toLowerCase();
        if (!trimmedEmail) {
            setError(t("auth.verify.emailRequired"));
            return;
        }
        if (resending || cooldown > 0) return;

        setError(null);
        setNotice(null);
        setResending(true);
        try {
            const result = await authApi.resendOtp(trimmedEmail);
            if (result.alreadyVerified) {
                setNotice(t("auth.verify.alreadyVerified"));
                return;
            }
            setNotice(t("auth.verify.resent"));
            setCooldown(RESEND_COOLDOWN_SECONDS);
        } catch (caught) {
            const mapped = resendErrorMessage(caught, t);
            setError(mapped.text);
            if (mapped.cooldown !== null) setCooldown(mapped.cooldown);
        } finally {
            setResending(false);
        }
    }

    function handleCodeChange(value: string) {
        setCode(value.replace(/\D/g, "").slice(0, 6));
    }

    async function handleBackToLogin() {
        setLeaving(true);
        try {
            if (isAuthenticated) await logout();
        } finally {
            navigate("/login", { replace: true });
        }
    }

    return (
        <div className="app-page-scaffold flex min-h-screen items-center justify-center px-4 py-10">
            <div className="app-auth-card w-full max-w-md rounded-3xl p-6 sm:p-8">
                <h1 className="app-text-primary text-2xl font-bold tracking-tight">
                    {t("auth.verify.title")}
                </h1>
                <p className="app-shell-text-muted mt-2 text-sm leading-relaxed">
                    {t("auth.verify.subtitle")}
                </p>

                <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
                    <Field label={t("auth.verify.email")} required>
                        <Input
                            type="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            placeholder={t("auth.signup.emailPlaceholder")}
                            autoComplete="email"
                            required
                        />
                    </Field>

                    <Field
                        label={t("auth.verify.code")}
                        hint={t("auth.verify.codeHint")}
                        required
                    >
                        <Input
                            value={code}
                            onChange={(event) => handleCodeChange(event.target.value)}
                            placeholder="123456"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            className="text-center text-2xl font-bold tracking-[0.5em]"
                            maxLength={6}
                            required
                        />
                    </Field>

                    {error && <Alert tone="error">{error}</Alert>}
                    {notice && <Alert tone="success">{notice}</Alert>}

                    <Button type="submit" loading={submitting} fullWidth>
                        {t("auth.verify.submit")}
                    </Button>

                    <Button
                        type="button"
                        variant="ghost"
                        fullWidth
                        loading={resending}
                        disabled={cooldown > 0}
                        onClick={handleResend}
                    >
                        {cooldown > 0
                            ? t("auth.verify.resendCooldown").replace("{s}", String(cooldown))
                            : t("auth.verify.resend")}
                    </Button>

                    <p className="text-center text-sm">
                        <button
                            type="button"
                            onClick={handleBackToLogin}
                            disabled={leaving}
                            className="app-shell-text-muted font-semibold text-[var(--color-primary)] disabled:opacity-50"
                        >
                            {t("auth.verify.backToLogin")}
                        </button>
                    </p>
                </form>
            </div>
        </div>
    );
}
